
import { trackEvent } from 'bits/analytics'
import { getTextFromClipboard, supportReadFromClipboardAPI } from 'bits/clipboard'
import { Component, Getter, Prop, State, Vue, Watch, namespace } from 'bits/vue'
import { SearchFilters } from 'bits/web_search'
import OzScreenSizeAwareCenterModal from 'library/v4/components/OzScreenSizeAwareCenterModal.vue'
import AiImageSearchSample from 'vuecomponents/AiImageSearchSample.vue'
import ContentPickerSearchBar from 'vuecomponents/content-picker-search-bar-old.vue'
import ContentPickerSearchModes from 'vuecomponents/content-picker-search-modes.vue'
import ContentPickerSearchResults from 'vuecomponents/content-picker-search-results.vue'
import ContentPickerSearchSuggestions from 'vuecomponents/content-picker-search-suggestions.vue'
import ContentSourceMenuList from 'vuecomponents/content-source-menu-list.vue'
import WebBrowserAttribution from 'vuecomponents/web_browser_attribution.vue'
import { ContentPickerSource } from 'vuexstore/modules/content_picker'

@Component({
  components: {
    OzScreenSizeAwareCenterModal,
    ContentPickerSearchBar,
    ContentSourceMenuList,
    ContentPickerSearchResults,
    ContentPickerSearchModes,
    ContentPickerSearchSuggestions,
    WebBrowserAttribution,
    AiImageSearchSample,
  },
})
export default class ContentSourceMenuNew extends Vue {
  $refs!: {
    searchBar: ContentPickerSearchBar
    searchModes: ContentPickerSearchModes
    gifAttribution: HTMLDivElement
  }

  @State isSafari!: boolean
  @State isTouchable!: boolean
  @State isEmbedded!: boolean
  @Getter dir!: string
  @Prop() cameraSupport!: boolean
  @Prop() videoSupport!: boolean
  @Prop() audioSupport!: boolean
  @Prop() screenshareSupport!: boolean
  @namespace('browser').Getter isPhone!: boolean
  @namespace('browser').Getter windowHeight!: number
  @namespace('browser').State darkMode!: boolean
  @namespace('contentPicker').Getter searchFilter!: SearchFilters
  @namespace('contentPicker').Getter xSearchScreen!: boolean
  @namespace('contentPicker').Getter isOpenedFromSearch!: boolean
  @namespace('contentPicker').Getter xSearchSuggestions!: boolean
  @namespace('contentPicker').Getter currentSearchResultsForFilter!: any[]
  @namespace('contentPicker').Getter isLinkInputMode!: boolean
  @namespace('contentPicker').Getter isAiImageSearchMode!: boolean
  @namespace('contentPicker').Getter xAiImageResults!: boolean
  @namespace('contentPicker').Getter xAiImagePreview!: boolean
  @namespace('contentPicker').Getter('searchQuery') storeSearchQuery!: string
  @namespace('contentPicker').Action syncSearchSuggestionsWithStorage!: () => void
  @namespace('contentPicker').Action pickSearchFilter!: (payload: { filter: SearchFilters }) => void
  @namespace('contentPicker').Action clearSearchFilter!: () => void
  @namespace('contentPicker').Action openSearch!: () => void
  @namespace('contentPicker').Action closeSearch!: () => void
  @namespace('contentPicker').Action setSearchQuery!: (payload: { query: string }) => void
  @namespace('contentPicker').Action startLinkInputMode!: () => void
  @namespace('contentPicker').Action openAiImagePreview!: () => void
  @namespace('contentPicker').Action closeAiImagePreview!: () => void
  @namespace('contentPicker').Action reopenAiImagePreview!: () => void

  currentClipboardText = ''
  CLIPBOARD_ENABLED = (!this.isPhone || (this.isPhone && !this.isSafari)) && supportReadFromClipboardAPI
  xClipboardButton = this.CLIPBOARD_ENABLED
  searchFocused = false
  isExpanded = false
  shouldWiggleSearchBar = false
  SearchFilters = SearchFilters

  // DIALOG

  @Watch('xSearchScreen', { immediate: true })
  handleSearchScreenShown(newVal: boolean): void {
    if (newVal && !this.isAiImageSearchMode) {
      if (this.isOpenedFromSearch) {
        setTimeout(() => {
          // Because we have an input field that needs focus, we delay the animation
          // until after the keyboard is popped up
          this.isExpanded = true
        }, 250)
      } else {
        this.isExpanded = true
      }
    } else {
      this.isExpanded = false
    }
  }

  closeMenu(): void {
    this.$emit('close')
  }

  handleScrimClick(extra: { type; event }): void {
    // Ignore scrim clicking for iPad
    if (this.searchFocused && this.isTouchable) return
    this.closeMenu()
  }

  menuListDisabled = false

  handleDragStart(): void {
    this.menuListDisabled = true
  }

  handleDragEnd(): void {
    this.menuListDisabled = false
  }

  // GREEN BUTTONS

  selectSource(source: ContentPickerSource): void {
    trackEvent('Content Menu New', 'Selected Source', source)
    this.shouldWiggleSearchBar = false
    this.$emit('select', source)
  }

  selectSearchResult(url: string): void {
    trackEvent('Content Menu New', 'Selected Search Result')
    this.$emit('select-link', url)
    this.$emit('close')
  }

  // SEARCHBAR

  get searchQuery(): string {
    return this.storeSearchQuery
  }

  set searchQuery(val) {
    this.setSearchQuery({ query: val })
  }

  get xBackButton(): boolean {
    return (this.xSearchScreen && !this.isOpenedFromSearch) || this.xAiImagePreview
  }

  async pasteText(): Promise<void> {
    try {
      this.searchQuery = (await getTextFromClipboard()).slice(0, 140)
      this.imitateSearchBarClickWithFilter(this.searchFilter || SearchFilters.Web)
    } catch (e) {
      // Reference: https://webkit.org/blog/10247/new-webkit-features-in-safari-13-1/, search for "Async Clipboard API"
      // Safari requires navigator.clipboard calls to be called within user gesture event handlers instead of a permissions-based model for reading from the clipboard
      // When getTextFromClipboard is called programatically like this, Safari will show a small "Paste" button on the UI to confirm if the user allows pasting
      // If they tap/click outside the button, Safari treat it as permission denied and throws a NotAllowedError => we can ignore it
      if (this.isSafari && e.name == 'NotAllowedError') {
        return
      }

      // throw unknown errors again so they will go to Sentry
      throw e
    }
  }

  focusSearchBar(): void {
    this.$nextTick(() => {
      if (!this.$refs.searchBar) return
      this.$refs.searchBar.focusSearchBar()
    })
  }

  focusSearchBarAfterPickFilter(): void {
    this.focusSearchBar()
  }

  // When user click on the searchbar, we open search menu and pre-pick the previous filter
  // or "None" filter to not pre-pick any.
  handleSearchBarClick(): void {
    if (!this.isLinkInputMode && !this.isAiImageSearchMode) {
      this.openSearch()
      if (!this.searchFilter) {
        this.pickSearchFilter({ filter: this.searchFilter || SearchFilters.Web })
      }
    }
    this.focusSearchBar()
  }

  handleSearchBarFocus(): void {
    this.syncClipboardText()
    this.searchFocused = true
  }

  handleSearchBarBlur(): void {
    this.searchFocused = false
  }

  // Clicking on a orange button will go to search menu with the appropriate filter
  // Clicking on clipboard will paste text and go to search menu
  imitateSearchBarClickWithFilter(filter: SearchFilters | undefined): void {
    if (!this.isLinkInputMode) {
      this.openSearch()
      this.pickSearchFilter({ filter: filter || this.searchFilter || SearchFilters.Web })
    }
    this.shouldWiggleSearchBar = false
    this.focusSearchBar()
  }

  triggerLinkInput(): void {
    this.startLinkInputMode()
    this.focusSearchBar()

    // wiggle the search bar again every time link input is triggered
    this.shouldWiggleSearchBar = false
    this.$nextTick(() => setTimeout(() => (this.shouldWiggleSearchBar = true)))
  }

  triggerAiImageSearch(): void {
    this.openAiImagePreview()
    this.focusSearchBar()
  }

  @Watch('searchQuery')
  /**
   *  If search query is being typed/change, likely when clicking paste button
   *  we make sure search menu is always shown
   */
  handleSearchQueryChangedOnMenuScreen(val): void {
    if (!this.xSearchScreen && val && val.trim() !== '') {
      this.imitateSearchBarClickWithFilter(this.searchFilter || SearchFilters.Web)
    }
  }

  goBack(): void {
    this.closeSearch()
    this.searchQuery = ''
    if (this.xAiImagePreview) {
      this.closeAiImagePreview()
      return
    }
    if (this.isAiImageSearchMode) {
      this.reopenAiImagePreview()
    } else {
      this.isExpanded = false
    }
  }

  handleSearchBarError(error: 'NO_SEARCH_FILTER'): void {
    if (error && error === 'NO_SEARCH_FILTER') {
      this.$refs.searchModes.shake()
    }
  }

  // CLIPBOARD

  async syncClipboardText(): Promise<void> {
    try {
      if (!this.isSafari && supportReadFromClipboardAPI) {
        this.currentClipboardText = await getTextFromClipboard()
        this.xClipboardButton =
          this.CLIPBOARD_ENABLED && !!this.currentClipboardText && this.currentClipboardText.trim().length > 0
      }
    } catch (e) {
      // do nothing
    }
  }

  // SEARCH QUERYING

  clearSearch(): void {
    if (this.isAiImageSearchMode) {
      this.goBack()
    } else {
      this.searchQuery = ''
    }
  }

  // SEARCH RESULTS

  mounted(): void {
    this.syncSearchSuggestionsWithStorage()
    this.syncClipboardText()
    document.addEventListener('visibilitychange', () => {
      this.syncClipboardText()
    })
  }
}
